import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, RefreshCw, Loader2, AlertTriangle, Clock, Gauge, TrendingDown, Bell, CreditCard, Tag, Sparkles, AlertCircle } from 'lucide-react'
import { api } from '../../api/client'
import { useTrackEvent } from '../../hooks/useTrackEvent'

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0)
const fmtK = (n: number) => Math.abs(n) >= 1000 ? `$${(n / 1000).toFixed(1)}k` : fmt(n)

interface Summary {
  total_spent: number
  total_income: number
  monthly_sub_cost: number
  active_subs: number
  total_transactions: number
  spending_by_category: { category: string; amount: number; color: string }[]
  monthly_trend: { month: string; amount: number }[]
  last_synced?: string
}

interface Account {
  name: string
  org: string
  balance: number
  available_balance?: number
  type?: string
}

interface AccountsResp {
  accounts: Account[]
  institutions: { name: string; accounts: Account[]; total_balance: number }[]
  plaid: { connected: boolean }
}

interface SyncStatus {
  status: string
  progress?: string
}

interface Insight {
  type: string
  icon: string
  color: string
  text: string
  detail: string
  action: string
}

interface DashboardInsights {
  kpis: {
    missed_rewards: number
    optimization_score: number
    monthly_leakage: number
    alerts: number
  }
  insights: Insight[]
}

const INSIGHT_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'alert-triangle': AlertTriangle,
  'credit-card': CreditCard,
  'tag': Tag,
  'clock': Clock,
  'alert-circle': AlertCircle,
  'sparkles': Sparkles,
}

const COLOR_MAP: Record<string, { border: string; bg: string; text: string; icon: string }> = {
  amber:   { border: 'border-amber-500/30',   bg: 'bg-amber-500/5',   text: 'text-amber-300',   icon: 'bg-amber-500/20 text-amber-400' },
  rose:    { border: 'border-rose-500/30',    bg: 'bg-rose-500/5',    text: 'text-rose-300',    icon: 'bg-rose-500/20 text-rose-400' },
  blue:    { border: 'border-blue-500/30',    bg: 'bg-blue-500/5',    text: 'text-blue-300',    icon: 'bg-blue-500/20 text-blue-400' },
  slate:   { border: 'border-slate-600/30',   bg: 'bg-slate-700/10',  text: 'text-slate-300',   icon: 'bg-slate-600/20 text-slate-400' },
  emerald: { border: 'border-emerald-500/30', bg: 'bg-emerald-500/5', text: 'text-emerald-300', icon: 'bg-emerald-500/20 text-emerald-400' },
}

function scoreColor(score: number) {
  if (score >= 80) return 'text-emerald-400'
  if (score >= 50) return 'text-amber-400'
  return 'text-rose-400'
}

function scoreRing(score: number) {
  if (score >= 80) return 'border-emerald-500/40'
  if (score >= 50) return 'border-amber-500/40'
  return 'border-rose-500/40'
}

function formatSyncTime(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZoneName: 'short',
  })
}

function syncAgeColor(dateStr: string): string {
  const diffHrs = (Date.now() - new Date(dateStr).getTime()) / 3600000
  if (diffHrs < 6) return 'text-emerald-400'
  if (diffHrs < 24) return 'text-amber-400'
  return 'text-rose-400'
}

export function DashboardPage() {
  const navigate = useNavigate()
  const trackEvent = useTrackEvent()
  const [summary, setSummary] = useState<Summary | null>(null)
  const [acctData, setAcctData] = useState<AccountsResp | null>(null)
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({ status: 'idle' })
  const [loading, setLoading] = useState(true)
  const [insights, setInsights] = useState<DashboardInsights | null>(null)

  const loadData = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        api.get<Summary>('/api/summary'),
        api.get<AccountsResp>('/api/accounts'),
      ])
      api.get<DashboardInsights>('/api/dashboard/insights').then(setInsights).catch(() => {})
      setSummary(s)
      setAcctData(a)
    } catch (e) { console.error('Failed to load dashboard', e) }
    setLoading(false)
  }, [])

  useEffect(() => { loadData() }, [loadData])

  // Poll sync status
  useEffect(() => {
    if (syncStatus.status !== 'running') return
    const iv = setInterval(async () => {
      try {
        const s = await api.get<SyncStatus>('/api/refresh/status')
        setSyncStatus(s)
        if (s.status !== 'running') { clearInterval(iv); loadData() }
      } catch { /* ignore */ }
    }, 3000)
    return () => clearInterval(iv)
  }, [syncStatus.status, loadData])

  const handleSync = async () => {
    try {
      setSyncStatus({ status: 'running', progress: 'Starting...' })
      await api.post('/api/refresh')
    } catch { setSyncStatus({ status: 'error' }) }
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>
  )

  const accounts = acctData?.accounts ?? []
  const lastSynced = summary?.last_synced
  const kpis = insights?.kpis
  const insightList = insights?.insights ?? []

  return (
    <div className="space-y-6">
      {/* Header + Sync */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div />
        <div className="flex items-center gap-3">
          {lastSynced && (
            <span className={`flex items-center gap-1 text-xs ${syncAgeColor(lastSynced)}`}>
              <Clock className="h-3.5 w-3.5" /> Synced {formatSyncTime(lastSynced)}
            </span>
          )}
          <button onClick={handleSync} disabled={syncStatus.status === 'running'}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50 transition-colors">
            <RefreshCw className={`h-4 w-4 ${syncStatus.status === 'running' ? 'animate-spin' : ''}`} />
            {syncStatus.status === 'running' ? (syncStatus.progress || 'Syncing...') : 'Sync Now'}
          </button>
        </div>
      </div>

      {/* ── KPI Tiles ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Missed Rewards — primary KPI */}
        <button onClick={() => { trackEvent('click', 'kpi_missed'); navigate('/rewards') }}
          className="group flex items-center gap-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 text-left transition-all hover:border-amber-500/50 hover:bg-amber-500/10 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 focus:ring-offset-slate-950">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-amber-500/20 text-amber-400">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-amber-400/70">Missed Rewards</p>
            <p className="mt-0.5 text-2xl font-bold text-amber-400">{fmtK(kpis?.missed_rewards ?? 0)}</p>
          </div>
          <ChevronRight className="h-5 w-5 flex-shrink-0 text-amber-600 group-hover:translate-x-0.5 group-hover:text-amber-400 transition-transform" />
        </button>

        {/* Optimization Score */}
        <button onClick={() => { trackEvent('click', 'kpi_score'); navigate('/rewards') }}
          className="group flex items-center gap-4 rounded-xl border border-slate-700/50 bg-slate-800/80 p-5 text-left transition-all hover:border-indigo-500/50 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 focus:ring-offset-slate-950">
          <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border-2 ${scoreRing(kpis?.optimization_score ?? 100)}`}>
            <Gauge className={`h-5 w-5 ${scoreColor(kpis?.optimization_score ?? 100)}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-400">Optimization</p>
            <p className={`mt-0.5 text-2xl font-bold ${scoreColor(kpis?.optimization_score ?? 100)}`}>
              {kpis?.optimization_score ?? '—'}<span className="text-base font-semibold text-slate-500">/100</span>
            </p>
          </div>
          <ChevronRight className="h-5 w-5 flex-shrink-0 text-slate-600 group-hover:translate-x-0.5 group-hover:text-indigo-400 transition-transform" />
        </button>

        {/* Monthly Leakage */}
        <button onClick={() => { trackEvent('click', 'kpi_leakage'); navigate('/analyze') }}
          className="group flex items-center gap-4 rounded-xl border border-rose-500/30 bg-rose-500/5 p-5 text-left transition-all hover:border-rose-500/50 hover:bg-rose-500/10 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-1 focus:ring-offset-slate-950">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-rose-500/20 text-rose-400">
            <TrendingDown className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-rose-400/70">Monthly Leakage</p>
            <p className="mt-0.5 text-2xl font-bold text-rose-400">{fmtK(kpis?.monthly_leakage ?? 0)}</p>
          </div>
          <ChevronRight className="h-5 w-5 flex-shrink-0 text-rose-600 group-hover:translate-x-0.5 group-hover:text-rose-400 transition-transform" />
        </button>

        {/* Alerts */}
        <button onClick={() => { trackEvent('click', 'kpi_alerts'); navigate('/monitor') }}
          className="group flex items-center gap-4 rounded-xl border border-slate-700/50 bg-slate-800/80 p-5 text-left transition-all hover:border-indigo-500/50 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 focus:ring-offset-slate-950">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-500/20 text-indigo-400">
            <Bell className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-400">Alerts</p>
            <p className="mt-0.5 text-2xl font-bold text-white">
              {kpis?.alerts ?? 0}
              <span className="text-base font-semibold text-slate-500"> active</span>
            </p>
          </div>
          <ChevronRight className="h-5 w-5 flex-shrink-0 text-slate-600 group-hover:translate-x-0.5 group-hover:text-indigo-400 transition-transform" />
        </button>
      </div>

      {/* ── Today's Insights ── */}
      {insightList.length > 0 && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-700/30">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-indigo-400" /> Today's Insights
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">What needs your attention right now</p>
          </div>
          <div className="divide-y divide-slate-700/20">
            {insightList.map((item, i) => {
              const colors = COLOR_MAP[item.color] || COLOR_MAP.slate
              const IconComp = INSIGHT_ICONS[item.icon] || AlertTriangle
              return (
                <button key={i} onClick={() => { trackEvent('click', `insight_${item.type}`); navigate(item.action) }}
                  className={`flex w-full items-center gap-4 px-5 py-4 text-left transition-all hover:${colors.bg} group`}>
                  <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${colors.icon}`}>
                    <IconComp className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${colors.text}`}>{item.text}</p>
                    {item.detail && <p className="text-xs text-slate-500 mt-0.5">{item.detail}</p>}
                  </div>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-600 group-hover:translate-x-0.5 group-hover:text-slate-400 transition-transform" />
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Account Balances ── */}
      <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-6">
        <h3 className="mb-4 text-lg font-semibold text-white">Account Balances</h3>
        {accounts.length ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {accounts.map((a, i) => {
              const nameLower = `${a.name} ${a.org}`.toLowerCase()
              const isLiability = a.balance < 0
                || (a.type === 'credit' || a.type === 'credit_card' || a.type === 'loan')
                || /credit|card|visa|mastercard|amex|discover|loan|mortgage|plat|sapphire|venture|freedom|propel/i.test(nameLower)
              const balColor = isLiability ? 'text-rose-400' : 'text-emerald-400'
              const borderAccent = isLiability ? 'border-rose-500/20' : 'border-emerald-500/20'
              return (
                <div key={i} className={`flex items-center justify-between rounded-lg border ${borderAccent} bg-slate-900/60 p-4`}>
                  <div>
                    <p className="font-semibold text-sm text-slate-200">{a.name}</p>
                    <p className="text-xs text-slate-500">{a.org}</p>
                  </div>
                  <p className={`text-lg font-bold ${balColor}`}>{fmt(Math.abs(a.balance))}</p>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="py-12 text-center">
            <p className="text-slate-500 text-sm">No accounts linked yet</p>
            <button onClick={() => navigate('/settings')} className="mt-3 text-sm font-medium text-indigo-400 hover:text-indigo-300">
              Connect your bank →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
